import IconButton from "@mui/material/IconButton";
import React, { useCallback, useEffect, useRef } from "react";

interface StepperButtonProps {
  // Applies a single step. Returns true when the step was accepted so the
  // press-and-hold loop knows whether to keep repeating.
  onStep: () => boolean;
  disabled: boolean;
  ariaLabel: string;
  children: React.ReactNode;
}

// 40x40 circular stepper button with press-and-hold auto-repeat. Shared between
// the worker Flexi signup card and the supervisor Force Assign popover.
const StepperButton: React.FC<StepperButtonProps> = ({
  onStep,
  disabled,
  ariaLabel,
  children,
}) => {
  const holdTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (holdTimeout.current) {
      clearTimeout(holdTimeout.current);
      holdTimeout.current = null;
    }
    if (holdInterval.current) {
      clearInterval(holdInterval.current);
      holdInterval.current = null;
    }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const startHold = () => {
    if (disabled) {
      return;
    }
    const applied = onStep();
    if (!applied) {
      return;
    }
    // Press-and-hold auto-repeat: after 500ms, repeat every 200ms.
    holdTimeout.current = setTimeout(() => {
      holdInterval.current = setInterval(() => {
        const ok = onStep();
        if (!ok) {
          clearTimers();
        }
      }, 200);
    }, 500);
  };

  return (
    <IconButton
      aria-label={ariaLabel}
      disabled={disabled}
      onPointerDown={startHold}
      onPointerUp={clearTimers}
      onPointerLeave={clearTimers}
      onClick={(e) => e.stopPropagation()}
      sx={{
        width: "40px",
        height: "40px",
        padding: "10px",
        border: disabled ? "1.5px solid #E0E0E0" : "1.5px solid #2F4D8B",
        backgroundColor: disabled ? "#F5F5F5" : "#FFFFFF",
        color: disabled ? "#9E9E9E" : "#2F4D8B",
        "&.Mui-disabled": {
          color: "#9E9E9E",
          border: "1.5px solid #E0E0E0",
          backgroundColor: "#F5F5F5",
        },
        "&:hover": {
          backgroundColor: disabled ? "#F5F5F5" : "#FFFFFF",
        },
      }}
    >
      {children}
    </IconButton>
  );
};

export default StepperButton;
